import React, { useEffect, useState } from 'react';
import { Chart, registerables } from 'chart.js';
import { LogoLoading } from 'ensightful-koala-ui';
import moment from 'moment';

import { useSnackbar } from 'contexts/snackbar';
import { useUser2 } from 'contexts/user2';
import { useViewport } from 'contexts/viewport';
import withResponsiveLayout from 'hoc/withResponsive';
import Mobile from 'pages/Activities/mobile';
import Tablet from 'pages/Activities/tablet';
import Desktop from 'pages/Activities/desktop';
import courseService from 'services/course';
import theme from 'shell/themes';
import { getUserPreferredFullName } from 'utils/helper';

Chart.register(...registerables);

const COLORS = [
    `${theme.palette.ensightful.primary[500]}`,
    `${theme.palette.ensightful.warning[500]}`,
    `${theme.palette.ensightful.success[500]}`,
    `${theme.palette.ensightful.error[500]}`,
    `${theme.palette.ensightful.secondary[200]}`,
];

const getGraphTitle = (type) => {
    return {
        login: 'Log-In Activity',
        'tasks-created': 'Tasks Created',
        'tasks-updates': 'Task Board Updates',
        'tasks-completed': 'Tasks Completed',
    }[type];
};

const getGraphSubtitle = (type) => {
    return {
        login: 'View the number of times each student has logged into the platform.',
        'tasks-created': 'View the number of tasks a student has personally created.',
        'tasks-updates':
            'View the amount of times a student has updated a task by changing its status, editing its details, or providing feedback.',
        'tasks-completed': 'View the number of owned tasks or subtasks a student has completed.',
    }[type];
};

const getDateRange = (startingMoment) => {
    return Array.from(Array(7))
        .map((_, index) => {
            const m = moment(startingMoment).subtract(index, 'day');
            return {
                moment: m,
                weekday: m.format('ddd'),
                date: m.format('D'),
            };
        })
        .reverse();
};

const getActivities = async (courseId, groupId, graphType, currentDateRange) => {
    const dateRangeStartEndTimestamps = currentDateRange.map(({ moment }) => {
        const dayStart = moment.hour(0).minute(0).second(0).millisecond(0).valueOf();
        const dayEnd = moment.hour(23).minute(59).second(59).millisecond(999).valueOf();
        return { start_on: dayStart, end_on: dayEnd };
    });
    return await courseService.findCourseActivitiesGraph({
        courseId,
        groupId,
        graphType,
        currentDateRange: dateRangeStartEndTimestamps,
    });
};

const renderExternalTooltip = (context, theme) => {
    let tooltipEl = document.getElementById('chartjs-tooltip');

    if (!tooltipEl) {
        tooltipEl = document.createElement('div');
        tooltipEl.id = 'chartjs-tooltip';
        tooltipEl.innerHTML = '<table style="width: 100%"></table>';
        document.body.appendChild(tooltipEl);
    }

    const tooltipModel = context.tooltip;
    if (tooltipModel.opacity === 0) {
        tooltipEl.style.opacity = 0;
        return;
    }

    tooltipEl.classList.remove('above', 'below', 'no-transform');
    if (tooltipModel.yAlign) {
        tooltipEl.classList.add(tooltipModel.yAlign);
    } else {
        tooltipEl.classList.add('no-transform');
    }

    if (tooltipModel.body) {
        const titleLines = tooltipModel.title;
        const bodyLines = tooltipModel.body.map(({ lines }) => lines).slice(0, -1);
        let innerHtml = '<thead>';
        titleLines.forEach((title) => {
            const splitTitle = title.split(',');
            innerHtml += '<tr><th>' + splitTitle[0] + ', ' + splitTitle[1] + '</th></tr>';
        });
        innerHtml += '</thead><tbody>';
        bodyLines.forEach((body, index) => {
            const colors = tooltipModel.labelColors[index];
            const splitBody = body[0].split(': ');
            innerHtml +=
                '<tr><td>' +
                `<div style="display: flex; align-items: center; justify-content: space-between; width: 100%;"><div style="display: flex; align-items: center; justify-content: space-between; gap: ${theme.spacing(
                    2
                )}"><div style="margin-bottom: ${theme.spacing(
                    0.5
                )}; border-radius: 50%; width: 10px; height: 10px; background: ${colors.backgroundColor};"></div>` +
                splitBody[0] +
                '</div><div>' +
                +Number(splitBody[1]).toFixed(2) +
                '</div></div></td></tr>';
        });
        innerHtml += '</tbody>';
        let tableRoot = tooltipEl.querySelector('table');
        tableRoot.innerHTML = innerHtml;
    }

    const position = context.chart.canvas.getBoundingClientRect();
    const tooltipWidth = 200;
    let tooltipLeft = position.left + tooltipModel.caretX - 0.5 * tooltipWidth;
    const tooltipLeftEdge = tooltipModel.caretX - 0.5 * tooltipWidth;
    const tooltipRightEdge = position.left + tooltipModel.caretX + 0.5 * tooltipWidth;
    const isTooFarLeft = tooltipLeftEdge < 0;
    const isTooFarRight = tooltipRightEdge > window.innerWidth;
    if (isTooFarLeft) tooltipLeft = position.left;
    if (isTooFarRight) tooltipLeft = tooltipLeft - (tooltipRightEdge - (context.chart.width + position.left));

    tooltipEl.style.position = 'absolute';
    tooltipEl.style.top = `${position.top - tooltipEl.offsetHeight}px`;
    tooltipEl.style.left = `${tooltipLeft}px`;
    tooltipEl.style.zIndex = theme.zIndex.drawer + 2;
    tooltipEl.style.borderRadius = '6px';
    tooltipEl.style.width = `${tooltipWidth}px`;
    tooltipEl.style.padding = theme.spacing(2);
    tooltipEl.style.backgroundColor = theme.palette.ensightful.shades[0];
    tooltipEl.style.boxShadow = theme.palette.ensightful.shadows[2];
    tooltipEl.style.opacity = 1;
    tooltipEl.style.pointerEvents = 'none';
};

const ActivitiesPage = () => {
    const { message } = useSnackbar();
    const { isLoading, course, currentGroup, currentGroupAccountUsers } = useUser2();
    const { width } = useViewport();

    const [state, setState] = useState({
        type: 'login',
        display: ['team'],
    });
    const [currentDateRange, setCurrentDateRange] = useState(getDateRange(moment()));
    const [activityData, setActivityData] = useState(null);

    const handleChange = (e, checked) => {
        setState((current) => {
            if (checked) {
                if (current.display.length >= 5) {
                    message.error('A maximum of 5 lines of data can be shown on the graph at a time.');
                    return current;
                }
                return { ...current, display: [...current.display, e.target.value] };
            } else {
                return { ...current, display: current.display.filter((key) => key !== e.target.value) };
            }
        });
    };

    const getActivitySets = () => {
        const data = [];
        if (activityData) {
            state.display.forEach((item) => {
                if (item === 'team') {
                    data.push({
                        activities: activityData.team,
                        label: 'Team Average',
                    });
                } else if (item === 'class') {
                    data.push({
                        activities: activityData.course,
                        label: 'Class Average',
                    });
                } else {
                    data.push({
                        activities: activityData[item],
                        label: getUserPreferredFullName(
                            currentGroupAccountUsers.find(({ owner_account_id }) => owner_account_id === item)
                        ),
                    });
                }
            });
        }
        return data.map((d, index) => ({ ...d, backgroundColor: COLORS[index] }));
    };

    const getLegendItem = (item, index) => {
        const getText = (item) => {
            if (item === 'team') return 'Team Average';
            if (item === 'class') return 'Class Average';
            return getUserPreferredFullName(
                currentGroupAccountUsers.find(({ owner_account_id }) => owner_account_id === item)
            );
        };
        return {
            text: getText(item),
            color: COLORS[index],
        };
    };

    useEffect(() => {
        if (course?.id && currentGroup) {
            (async () => {
                setActivityData(null);
                const activityData = await getActivities(course.id, currentGroup.id, state.type, currentDateRange);
                setActivityData(activityData);
            })();
        }
    }, [course?.id, currentGroup, state.type, currentDateRange, getActivities]);

    const props = {
        state,
        setState,
        handleChange,
        currentDateRange,
        setCurrentDateRange,
        activitySets: activityData ? getActivitySets() : null,
        getGraphTitle,
        getGraphSubtitle,
        getDateRange,
        renderExternalTooltip,
        getLegendItem,
    };

    const responsiveLayouts = {
        mobile: <Mobile {...props} />,
        tablet: <Tablet {...props} />,
        desktop: <Desktop {...props} />,
    };

    return isLoading || !currentGroup || !currentGroupAccountUsers ? (
        <LogoLoading />
    ) : (
        <>{withResponsiveLayout(width, responsiveLayouts)}</>
    );
};

export default ActivitiesPage;
